import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import PortalLayout from '@/components/layouts/PortalLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { portalAPI } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, HelpCircle, Plus, Minus, Trash2, AlertTriangle, Clock } from 'lucide-react';

const unitTypes = [
  { value: 'standard', label: 'Standard Unit', description: 'Standard portable toilet' },
  { value: 'ADA', label: 'ADA-Compliant Unit', description: 'Wheelchair accessible' },
  { value: 'handwash', label: 'Handwash Station', description: 'Standalone sink' },
];

// Calculate days until a date
const getDaysUntil = (dateString) => {
  if (!dateString) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateString);
  const diffTime = targetDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get urgency level based on days until start
const getUrgencyLevel = (dateString) => {
  const days = getDaysUntil(dateString);
  if (days === null) return null;
  if (days < 2) return 'rush'; // Under 48 hours
  if (days < 7) return 'short'; // 2-7 days
  return 'normal'; // 7+ days
};

export default function RequestQuote() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    company_name: '',
    email: '',
    phone: '',
    event_type: '',
    site_address: '',
    start_date: '',
    end_date: '',
    notes: '',
  });
  
  // Multiple unit types support
  const [unitSelections, setUnitSelections] = useState([
    { type: 'standard', quantity: 1 }
  ]);

  // Calculate urgency based on start date
  const urgencyLevel = useMemo(() => getUrgencyLevel(formData.start_date), [formData.start_date]);
  const daysUntilStart = useMemo(() => getDaysUntil(formData.start_date), [formData.start_date]);

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addUnitType = () => {
    // Find a unit type that hasn't been added yet
    const usedTypes = unitSelections.map(s => s.type);
    const availableType = unitTypes.find(t => !usedTypes.includes(t.value));
    
    if (availableType) {
      setUnitSelections(prev => [...prev, { type: availableType.value, quantity: 1 }]);
    }
  };

  const removeUnitType = (index) => {
    if (unitSelections.length > 1) {
      setUnitSelections(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateUnitSelection = (index, field, value) => {
    setUnitSelections(prev => prev.map((selection, i) => 
      i === index ? { ...selection, [field]: value } : selection
    ));
  };

  const incrementQuantity = (index) => {
    setUnitSelections(prev => prev.map((selection, i) => 
      i === index ? { ...selection, quantity: selection.quantity + 1 } : selection
    ));
  };

  const decrementQuantity = (index) => {
    setUnitSelections(prev => prev.map((selection, i) => 
      i === index && selection.quantity > 1 
        ? { ...selection, quantity: selection.quantity - 1 } 
        : selection
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Format unit selections into notes
      const unitSummary = unitSelections
        .map(s => {
          const unitType = unitTypes.find(t => t.value === s.type);
          return `${s.quantity} ${unitType?.label || s.type}`;
        })
        .join(', ');

      const totalUnits = unitSelections.reduce((sum, s) => sum + s.quantity, 0);
      const primaryUnitType = unitSelections[0]?.type || 'standard';

      const notesWithUnits = `Units requested: ${unitSummary}${formData.notes ? `\n\nAdditional notes: ${formData.notes}` : ''}`;

      await portalAPI.submitQuote({
        ...formData,
        unit_type: primaryUnitType,
        units_requested: totalUnits,
        notes: notesWithUnits,
      });
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PortalLayout>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-emerald-600" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4" data-testid="success-title">
                Quote Request Submitted!
              </h2>
              <p className="text-slate-600 mb-8">
                Thank you for your interest in Tinkle Tent Rentals. We've received your quote request and will contact you within 24 hours with pricing information.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-[#194461] hover:bg-[#091D2E]"
                  data-testid="back-home-btn"
                >
                  Back to Home
                </Button>
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      customer_name: '',
                      company_name: '',
                      email: '',
                      phone: '',
                      event_type: '',
                      site_address: '',
                      start_date: '',
                      end_date: '',
                      notes: '',
                    });
                    setUnitSelections([{ type: 'standard', quantity: 1 }]);
                  }}
                  variant="outline"
                  data-testid="submit-another-btn"
                >
                  Submit Another Request
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </PortalLayout>
    );
  }

  const usedTypes = unitSelections.map(s => s.type);
  const canAddMore = unitSelections.length < unitTypes.length;

  return (
    <PortalLayout>
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card className="border-slate-200">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl" data-testid="form-title">Request a Quote</CardTitle>
            <CardDescription>
              Fill out the form below and we'll get back to you with pricing within 24 hours.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Contact Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Your Name *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      data-testid="input-customer-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_name">Company Name (Optional)</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="ABC Construction"
                      data-testid="input-company-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(423) 555-0100"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Rental Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Rental Details</h3>
                <div>
                  <Label htmlFor="event_type">Type of Use *</Label>
                  <Select 
                    value={formData.event_type} 
                    onValueChange={(value) => handleSelectChange('event_type', value)}
                    required
                  >
                    <SelectTrigger data-testid="select-event-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="construction">Construction Site</SelectItem>
                      <SelectItem value="event">Special Event</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="site_address">Site Address *</Label>
                  <Input
                    id="site_address"
                    name="site_address"
                    value={formData.site_address}
                    onChange={handleChange}
                    required
                    placeholder="123 Main St, Johnson City, TN 37601"
                    data-testid="input-site-address"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      name="start_date"
                      type="date"
                      min={today}
                      value={formData.start_date}
                      onChange={handleChange}
                      required
                      data-testid="input-start-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end_date">End Date *</Label>
                    <Input
                      id="end_date"
                      name="end_date"
                      type="date"
                      min={formData.start_date || today}
                      value={formData.end_date}
                      onChange={handleChange}
                      required
                      data-testid="input-end-date"
                    />
                  </div>
                </div>

                {/* Urgency Warning */}
                {urgencyLevel === 'rush' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="rush-warning">
                    <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-semibold text-red-800">Rush Request - Less than 48 hours notice</p>
                      <p className="text-sm text-red-700 mt-1">
                        We may not be able to fulfill orders with less than 48 hours notice. 
                        Please call us at <a href="tel:4235550100" className="font-medium underline">(423) 555-0100</a> to confirm availability.
                      </p>
                    </div>
                  </div>
                )}

                {urgencyLevel === 'short' && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="short-notice-warning">
                    <Clock className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-semibold text-amber-800">Short Notice Request</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Your requested start date is within 7 days. We'll do our best to accommodate, 
                        but availability may be limited. We recommend booking at least 1 week in advance.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Unit Selection */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Units Needed</h3>
                  <Link 
                    to="/portal/planning-guide" 
                    className="text-sm text-[#52B8E1] hover:text-[#194461] flex items-center gap-1"
                    data-testid="planning-guide-link"
                  >
                    <HelpCircle size={16} />
                    Need help planning?
                  </Link>
                </div>

                <div className="space-y-3">
                  {unitSelections.map((selection, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200"
                      data-testid={`unit-selection-${index}`}
                    >
                      <div className="flex-1">
                        <Select 
                          value={selection.type}
                          onValueChange={(value) => updateUnitSelection(index, 'type', value)}
                        >
                          <SelectTrigger data-testid={`select-unit-type-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {unitTypes.map(type => (
                              <SelectItem 
                                key={type.value} 
                                value={type.value}
                                disabled={usedTypes.includes(type.value) && selection.type !== type.value}
                              >
                                <div>
                                  <span className="font-medium">{type.label}</span>
                                  <span className="text-slate-500 text-xs ml-2">({type.description})</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => decrementQuantity(index)}
                          disabled={selection.quantity <= 1}
                          data-testid={`decrement-${index}`}
                        >
                          <Minus size={16} />
                        </Button>
                        <span className="w-8 text-center font-semibold text-lg">
                          {selection.quantity}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => incrementQuantity(index)}
                          data-testid={`increment-${index}`}
                        >
                          <Plus size={16} />
                        </Button>
                      </div>

                      {unitSelections.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-slate-400 hover:text-red-500"
                          onClick={() => removeUnitType(index)}
                          data-testid={`remove-${index}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {canAddMore && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed"
                    onClick={addUnitType}
                    data-testid="add-unit-type-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    Add Another Unit Type
                  </Button>
                )}

                {/* Unit Summary */}
                <div className="p-3 bg-[#52B8E1]/10 rounded-lg border border-[#52B8E1]/20">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">Total units: </span>
                    {unitSelections.reduce((sum, s) => sum + s.quantity, 0)} 
                    <span className="text-slate-500 ml-2">
                      ({unitSelections.map(s => {
                        const type = unitTypes.find(t => t.value === s.type);
                        return `${s.quantity} ${type?.label.replace(' Unit', '').replace(' Station', '')}`;
                      }).join(', ')})
                    </span>
                  </p>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any special requirements or questions..."
                  rows={4}
                  data-testid="input-notes"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#194461] hover:bg-[#091D2E]"
                disabled={loading}
                data-testid="submit-quote-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Quote Request'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
